import React, { useState, useContext, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MyContext } from "../../../../MyContextApi";
import "./../../../../styles/KeywordChips.css";

import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import { Box } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { modelsApi } from "../../../../API/axiosInstance";


/* ─── Chip Action Dialog ─────────────────────────────────────────────────── */
function ChipActionDialog({ open, onClose, keyword, onTopic, onSession }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontFamily: "'Host Grotesk', sans-serif",
          color: "#2c4e80",
        }}
      >
        <InfoIcon color="info" />
        Create Session or Topic
      </DialogTitle>

      <Box px={3} pb={1}>
        <Typography
          variant="body1"
          sx={{ fontFamily: "'Host Grotesk', sans-serif" }}
        >
          How would you like to use <strong>&ldquo;{keyword}&rdquo;</strong>?
        </Typography>
      </Box>
      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          pt: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1.5,
        }}
      >
        <Box>
          <Button
            fullWidth
            variant="outlined"
            onClick={onSession}
            sx={{ fontFamily: "'Host Grotesk', sans-serif" }}
          >
            Create Session
          </Button>

          <Box
            component="ul"
            sx={{
              mt: 0.5,
              pl: 2,
              mb: 0,
              color: "text.secondary",
              opacity: 0.7,
              fontFamily: "'Host Grotesk', sans-serif",
              fontSize: "0.75rem",
            }}
          >
            <li>Runs an on-demand crawl on Twitter</li>
            <li>Fetches up to 100 posts</li>
            <li>You’ll be redirected to the DataHub</li>
          </Box>
        </Box>

        <Box>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={onTopic}
            sx={{ fontFamily: "'Host Grotesk', sans-serif" }}
          >
            Create Topic
          </Button>

          <Box
            component="ul"
            sx={{
              mt: 0.5,
              pl: 2,
              mb: 0,
              color: "text.secondary",
              opacity: 0.7,
              fontFamily: "'Host Grotesk', sans-serif",
              fontSize: "0.75rem",
            }}
          >
            <li>Starts a broader, auto-crawled topic</li>
            <li>You can edit this topic in the Topics Manager</li>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

ChipActionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  keyword: PropTypes.string,
  onTopic: PropTypes.func.isRequired,
  onSession: PropTypes.func.isRequired,
};

/* ─── KeywordChips ───────────────────────────────────────────────────────── */
const KeywordChips = ({ data = {} }) => {
  const { setKeywordInput, formData, setFormData } = useContext(MyContext);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("Latest");
  const [hoveredChip, setHoveredChip] = useState(null);
  const [topPerSection, setTopPerSection] = useState(5);
  const [latestDuration, setLatestDuration] = useState("most recent");
  const [latestData, setLatestData] = useState(null);
  const [isLoadingLatest, setIsLoadingLatest] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState(null);

  const tabs = ["Latest", "All Time", "Section"];
  const token = localStorage.getItem("Token");

  const durationOptions = [
    { label: "Most Recent", value: "most recent" },
    ...Array.from({ length: 15 }, (_, i) => ({
      label: `${i + 1} hour${i === 0 ? "" : "s"} ago`,
      value: `${i + 1} hour${i === 0 ? "" : "s"} ago`,
    })),
  ];

  const chipColors = [
    { bg: "#FEF3C7", border: "#FCD34D", text: "#92400E", badge: "#FBBF24" },
    { bg: "#DBEAFE", border: "#93C5FD", text: "#1E3A8A", badge: "#3B82F6" },
    { bg: "#FCE7F3", border: "#F9A8D4", text: "#831843", badge: "#EC4899" },
    { bg: "#D1FAE5", border: "#6EE7B7", text: "#065F46", badge: "#10B981" },
    { bg: "#E0E7FF", border: "#A5B4FC", text: "#3730A3", badge: "#6366F1" },
    { bg: "#FED7AA", border: "#FDBA74", text: "#7C2D12", badge: "#F97316" },
    { bg: "#E9D5FF", border: "#C084FC", text: "#581C87", badge: "#A855F7" },
    { bg: "#CCFBF1", border: "#5EEAD4", text: "#134E4A", badge: "#14B8A6" },
    { bg: "#FFE4E6", border: "#FDA4AF", text: "#881337", badge: "#F43F5E" },
    { bg: "#FEF9C3", border: "#FDE047", text: "#713F12", badge: "#EAB308" },
  ];

  const safeData = data && typeof data === "object" ? data : {};
  const allTimeSource =
    safeData.totals ||
    safeData.byKeyword ||
    (Object.keys(safeData).length ? safeData : {});
  const allTimeEntries = Object.entries(allTimeSource);
  const sectionsObj = safeData.sections || {};

  const sectionEntries = useMemo(
    () =>
      Object.entries(sectionsObj).map(([sectionName, kwList]) => {
        const arr = Array.isArray(kwList) ? [...kwList] : [];
        arr.sort((a, b) => {
          const ac = a.sectionCount ?? a.count ?? 0;
          const bc = b.sectionCount ?? b.count ?? 0;
          return bc - ac;
        });
        return [sectionName, arr];
      }),
    [sectionsObj],
  );

  const fetchLatestTrends = async (timestamp) => {
    setIsLoadingLatest(true);

    try {
      const response = await modelsApi.get(`/trends24`, {
        params: {
          timestamp: timestamp,
        },
      });

      setLatestData(response.data);
    } catch (error) {
      console.error("Error fetching latest trends:", error);
      setLatestData(null);
    } finally {
      setIsLoadingLatest(false);
    }
  };

  useEffect(() => {
    if (activeTab === "Latest") fetchLatestTrends(latestDuration);
  }, [activeTab, latestDuration]);

  const latestEntries = useMemo(() => {
    if (!latestData || !latestData.trends) return [];
    return Object.entries(latestData.trends);
  }, [latestData]);

  const handleChipClick = (keyword) => {
    setSelectedKeyword(keyword);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedKeyword(null);
  };

  const handleTopic = () => {
    if (typeof setKeywordInput === "function") {
      setKeywordInput(selectedKeyword);
      setFormData({
        ...formData,
        topic: selectedKeyword,
        platforms: ["twitter", "facebook", "youtube", "reddit", "threads"],
        type: "section",
      });
    }
    handleDialogClose();
    navigate("/automatedCrawler", { state: { autoRun: true } });
  };

  const handleSession = () => {
    handleDialogClose();
    navigate("/socialMediaCrawler", {
      state: {
        autoRun: true,
        sessionPayload: {
          filter: "latest",
          keyword: selectedKeyword,
          platform: ["twitter"],
          postCount: "100",
          type: "section",
        },
      },
    });
  };

  const renderChip = (keyword, count, colorScheme, key) => (
    <div
      key={key}
      className="keyword-chips-wrapper"
      onMouseEnter={() => setHoveredChip(keyword)}
      onMouseLeave={() => setHoveredChip(null)}
      onClick={() => handleChipClick(keyword)}
    >
      <div
        className="keyword-chip"
        style={{
          backgroundColor: colorScheme.bg,
          borderColor: colorScheme.border,
          color: colorScheme.text,
        }}
      >
        <span>{keyword}</span>
        {count !== 0 && (
          <span
            className="keyword-chip-badge"
            style={{ backgroundColor: colorScheme.badge }}
          >
            {count}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="keyword-chips-outer-container">
      <ChipActionDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        keyword={selectedKeyword}
        onTopic={handleTopic}
        onSession={handleSession}
      />

      <div className="keyword-chips-header-area">
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div className="keyword-chips-tab-container">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`keyword-chips-tab ${activeTab === tab ? "active" : ""}`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {activeTab === "Section" && (
          <div className="keyword-chips-control-card">
            <div className="keyword-chips-control-label">Top:</div>
            <select
              className="keyword-chips-small-select"
              value={topPerSection}
              onChange={(e) => setTopPerSection(Number(e.target.value))}
            >
              {Array.from({ length: 15 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        )}

        {activeTab === "Latest" && (
          <div className="keyword-chips-control-card">
            <div className="keyword-chips-control-label">Duration:</div>
            <select
              className="keyword-chips-small-select"
              value={latestDuration}
              onChange={(e) => setLatestDuration(e.target.value)}
            >
              {durationOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="keyword-chips-scroll-area">
        {activeTab === "Latest" && (
          <div className="keyword-chips-container">
            {isLoadingLatest ? (
              <div className="keyword-chips-empty-message">
                Loading latest trends...
              </div>
            ) : latestEntries.length === 0 ? (
              <div className="keyword-chips-empty-message">
                No latest trends available.
              </div>
            ) : (
              latestEntries.map(([keyword, count], index) =>
                renderChip(
                  keyword,
                  count,
                  chipColors[index % chipColors.length],
                  keyword,
                ),
              )
            )}
          </div>
        )}

        {activeTab === "All Time" && (
          <div className="keyword-chips-container">
            {allTimeEntries.length === 0 ? (
              <div className="keyword-chips-empty-message">
                No keywords available.
              </div>
            ) : (
              allTimeEntries.map(([keyword, count], index) =>
                renderChip(
                  keyword,
                  count,
                  chipColors[index % chipColors.length],
                  keyword,
                ),
              )
            )}
          </div>
        )}

        {activeTab === "Section" && (
          <>
            {sectionEntries.length === 0 ? (
              <div className="keyword-chips-empty-message">
                No section data available.
              </div>
            ) : (
              sectionEntries.map(([sectionName, kwList]) =>
                kwList.slice(0, topPerSection).length === 0 ? null : (
                  <div
                    key={sectionName}
                    className="keyword-chips-section-block"
                  >
                    <div className="keyword-chips-section-header">
                      {sectionName}
                    </div>
                    <div className="keyword-chips-container">
                      {kwList.slice(0, topPerSection).map((kwObj, idx) => {
                        const keyword = kwObj.keyword || kwObj;
                        const sectionCount =
                          kwObj.sectionCount ?? kwObj.count ?? 0;
                        return renderChip(
                          keyword,
                          sectionCount,
                          chipColors[idx % chipColors.length],
                          `${sectionName}-${keyword}`,
                        );
                      })}
                    </div>
                  </div>
                ),
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default KeywordChips;
